import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { authApi } from '@/api/auth';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { AuthShell } from './LoginPage';
import { humanizeError } from '@/lib/errors';
import { isStoreBuild } from '@/lib/platform';
import { safeNext } from '@/lib/guestMode';

type RegisterFormData = {
  full_name: string;
  email: string;
  phone: string;
};

/**
 * Телефон: цифры, пробелы, скобки, дефисы и ведущий «+». Иностранные номера
 * принимаем как есть — ревьюер магазина регистрируется со своим, и запрет на
 * всё, кроме «+7», закрыл бы ему вход.
 */
const PHONE_RE = /^\+?[\d\s()-]{9,20}$/;

export function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Куда гость нажимал до регистрации (гостевой режим, 14.09.2026): курс, словарь. Только /app/….
  const next = safeNext((useLocation().state as { next?: unknown } | null)?.next);
  const currentUser = useAuthStore((s) => s.user);
  const isAuthed = useAuthStore((s) => Boolean(s.accessToken));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormData>();

  // На сайте телефон обязателен — решение владельца 14.09.2026, школе нужен
  // номер для связи. В приложении из магазина обязательным его делать нельзя:
  // Apple по правилу 5.1.1 требует собирать только то, без чего приложение не
  // работает, а учиться можно и без звонка.
  //
  // ⚠️ Подписи «Куратор свяжется с вами по этому номеру» под полем больше нет
  // (14.09.2026, Apple 3.1.3(f)): новому ученику никто не звонит, а обещание
  // звонка читается как отдел продаж.
  const phoneRequired = !isStoreBuild();

  // Already signed in → no need to register again.
  // Неподтверждённая почта ведёт на экран кода, а не в платформу. Этот же
  // страж срабатывает сразу после успешной регистрации — setAuth перерисовывает
  // компонент раньше, чем выполнится navigate ниже, и раньше страж уводил
  // новичка прямо в платформу, минуя ввод кода.
  if (isAuthed && currentUser) {
    const landing = !currentUser.email_verified_at
      ? '/verify-email'
      : currentUser.role === 'admin' ? '/admin' :
        currentUser.role === 'curator' ? '/curator' : '/app';
    return <Navigate to={landing} replace />;
  }

  const onSubmit = async (data: RegisterFormData) => {
    setSubmitting(true);
    setError(null);
    try {
      // Пароля человек не вводит: аккаунт создаётся без него, а вход — кодом из
      // письма (как в Edvibe). Регистрация сама отправляет код, поэтому на
      // следующем экране письмо уже ждёт — второй раз не просим.
      await authApi.register({
        full_name: data.full_name,
        email: data.email,
        phone: data.phone?.trim() || undefined,
      });
      navigate('/enter-code', { replace: true, state: { email: data.email, codeAlreadySent: true, next } });
    } catch (e) {
      setError(humanizeError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell title={t('auth.signUp')}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          label={t('auth.fullName')}
          autoComplete="name"
          {...register('full_name', { required: true, minLength: 2 })}
          error={errors.full_name && t('auth.errorFullName')}
        />
        <Input
          type="email"
          autoComplete="email"
          label={t('auth.email')}
          {...register('email', { required: true, pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/ })}
          error={errors.email && t('auth.errorEmailInvalid')}
        />
        <Input
          type="tel"
          autoComplete="tel"
          inputMode="tel"
          label={phoneRequired ? t('auth.phone') : t('auth.phoneOptional')}
          placeholder="+7 700 000 00 00"
          {...register('phone', {
            required: phoneRequired,
            validate: (v) => !v?.trim() || PHONE_RE.test(v.trim()),
          })}
          error={errors.phone && t('auth.errorPhone')}
        />

        {error && <p className="text-sm text-error">{error}</p>}

        <Button type="submit" size="lg" fullWidth loading={submitting}>
          {t('auth.signUp')}
        </Button>

        <p className="pt-2 text-center text-sm text-on-surface-variant">
          {t('auth.haveAccount')}{' '}
          <Link to="/login" replace className="font-semibold text-primary">
            {t('auth.signIn')}
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}
